import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Clock, FileText, CheckCircle, AlertTriangle, Loader2, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { getMyJDGenerations, cancelJDGeneration, deleteJDGeneration } from '../../../../api/client';
import ConfirmationDialog from '../../../common/ConfirmationDialog';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const looksLikeGeneratedBodyText = (value = '') => {
  const normalized = String(value).trim();
  if (!normalized) return false;

  return (
    normalized.length > 120 ||
    normalized.includes('\n') ||
    /function\s*\(|const\s+\w+\s*=|let\s+\w+\s*=|var\s+\w+\s*=|=>|import\s+.+from|export\s+default/i.test(normalized)
  );
};

const getDraftDisplayName = (draft, t) => {
  const candidates = [
    draft?.structured_description?.title,
    draft?.job_title,
    draft?.jd_title,
    draft?.title,
    draft?.file_name,
    draft?.audio_file_name,
    draft?.source_name,
  ];

  const firstValid = candidates.find((candidate) => {
    const value = String(candidate || '').trim();
    return value && !looksLikeGeneratedBodyText(value);
  });

  if (firstValid) {
    return firstValid;
  }

  const fallbackFileName = String(draft?.file_name || draft?.audio_file_name || '').trim();
  if (fallbackFileName) {
    return fallbackFileName.replace(/\.[^/.]+$/, '');
  }

  return t('jobDescription.draftsModal.untitledJD', 'Untitled JD');
};

export default function JDDraftsModal({ open, onClose, onSelectDraft }) {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [draftPendingDelete, setDraftPendingDelete] = useState(null);
  const initialLoadRef = useRef(false);

  const fetchDrafts = useCallback(async (targetPage = page, targetPageSize = pageSize, showOverlay = false) => {
    if (!open) return;

    setError('');
    if (showOverlay) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const data = await getMyJDGenerations({ page: targetPage, pageSize: targetPageSize });
      const nextTotalPages = Math.max(1, data.totalPages || Math.ceil((data.count || 0) / targetPageSize) || 1);
      const safePage = Math.min(Math.max(targetPage, 1), nextTotalPages);

      setDrafts(data.results || []);
      setTotalCount(data.count || 0);
      setTotalPages(nextTotalPages);

      if (safePage !== targetPage) {
        setPage(safePage);
      }
    } catch (err) {
      console.error('Failed to fetch JD drafts', err);
      setError(t('jobDescription.draftsModal.errors.loadFailed'));
      setDrafts([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [open, page, pageSize]);

  useEffect(() => {
    if (!open) return;
    setError('');
    initialLoadRef.current = true;
    setPage(1);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const showOverlay = initialLoadRef.current;
    initialLoadRef.current = false;
    fetchDrafts(page, pageSize, showOverlay);
  }, [open, page, pageSize, fetchDrafts]);

  const handleCancel = async (draftId, e) => {
    e.stopPropagation();
    try {
      await cancelJDGeneration(draftId);
      await fetchDrafts(page, pageSize, false);
    } catch (err) {
      console.error(err);
      setError(t('jobDescription.draftsModal.errors.cancelFailed'));
    }
  };

  const openDeleteConfirmation = (draft, e) => {
    e.stopPropagation();
    setDraftPendingDelete(draft);
  };

  const closeDeleteConfirmation = () => {
    if (deletingDraftId) return;
    setDraftPendingDelete(null);
  };

  const handleDeleteDraft = async () => {
    if (!draftPendingDelete) return;

    setDeletingDraftId(draftPendingDelete.id);
    setError('');

    try {
      await deleteJDGeneration(draftPendingDelete.id);
      setDraftPendingDelete(null);

      const targetPage = drafts.length === 1 && page > 1 ? page - 1 : page;
      if (targetPage !== page) {
        setPage(targetPage);
      } else {
        await fetchDrafts(targetPage, pageSize, false);
      }
    } catch (err) {
      console.error('Failed to delete JD draft', err);
      setError(t('jobDescription.draftsModal.errors.deleteFailed'));
    } finally {
      setDeletingDraftId(null);
    }
  };

  if (!open) return null;

  const formatDate = (isoString) => {
    const d = new Date(isoString);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'done':
        return <span className="flex items-center gap-1 text-xs font-semibold px-2 py-1 bg-green-100 text-green-700 rounded-lg"><CheckCircle size={12} /> {t('jobDescription.draftsModal.status.ready')}</span>;
      case 'error':
        return <span className="flex items-center gap-1 text-xs font-semibold px-2 py-1 bg-red-100 text-red-700 rounded-lg"><AlertTriangle size={12} /> {t('jobDescription.draftsModal.status.failed')}</span>;
      default:
        return <span className="flex items-center gap-1 text-xs font-semibold px-2 py-1 bg-blue-100 text-blue-700 rounded-lg"><Loader2 size={12} className="animate-spin" /> {t('jobDescription.draftsModal.status.processing')}</span>;
    }
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  const handlePageSizeChange = (event) => {
    const nextPageSize = Number(event.target.value);
    setPageSize(nextPageSize);
  };

  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[999999]">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl flex flex-col h-[min(85vh,720px)]">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50">
              <Clock className="text-blue-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{t('jobDescription.draftsModal.title')}</h2>
              <p className="text-sm text-gray-500">{t('jobDescription.draftsModal.subtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-gray-50/70">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{totalCount}</span>
            <span>{t('jobDescription.draftsModal.pagination.draftsCount', { count: totalCount })}</span>
            {totalCount > 0 && (
              <span className="text-gray-500">{t('jobDescription.draftsModal.pagination.showing', { start: startItem, end: endItem })}</span>
            )}
            {refreshing && !loading && <Loader2 size={14} className="animate-spin text-blue-500" />}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span>{t('jobDescription.draftsModal.pagination.show')}</span>
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 outline-none focus:ring-2 focus:ring-blue-500"
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 relative">
          {loading ? (
            <div className="h-full min-h-[280px] flex flex-col items-center justify-center gap-3">
              <Loader2 className="animate-spin text-blue-500" size={32} />
              <p className="text-gray-500 font-medium">{t('jobDescription.draftsModal.loading')}</p>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 text-red-600 rounded-xl flex items-center gap-2">
              <AlertTriangle size={18} /> {error}
            </div>
          ) : drafts.length === 0 ? (
            <div className="h-full min-h-[280px] flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <FileText className="text-gray-300" size={24} />
              </div>
              <p className="text-gray-500 font-medium">{t('jobDescription.draftsModal.emptyState')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('jobDescription.draftsModal.emptyStateHint')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {drafts.map((draft) => {
                const draftDisplayName = getDraftDisplayName(draft, t);

                return (
                <div
                  key={draft.id}
                  onClick={() => draft.status === 'done' || draft.status === 'error' ? onSelectDraft(draft.id) : null}
                  className={`group flex items-center gap-4 p-4 border rounded-2xl transition-all ${draft.status === 'done' || draft.status === 'error' ? 'cursor-pointer hover:border-blue-300 hover:shadow-md hover:bg-blue-50/30' : 'opacity-75 cursor-not-allowed bg-gray-50'
                    }`}
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <FileText size={18} className="text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <p className="font-semibold text-gray-900 truncate" title={draftDisplayName}>
                        {draftDisplayName}
                      </p>
                      {getStatusBadge(draft.status)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{formatDate(draft.created_at)}</span>
                      <span>•</span>
                      <span className="truncate">{draft.source_type === 'audio' ? t('jobDescription.draftsModal.sourceType.audio') : t('jobDescription.draftsModal.sourceType.transcript')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {(draft.status === 'processing' || draft.status === 'pending') && (
                      <button
                        onClick={(e) => handleCancel(draft.id, e)}
                        className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        {t('jobDescription.draftsModal.actions.cancel')}
                      </button>
                    )}
                    <button
                      onClick={(e) => openDeleteConfirmation(draft, e)}
                      disabled={deletingDraftId === draft.id}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label={t('jobDescription.draftsModal.actions.delete')}
                      title={t('jobDescription.draftsModal.actions.deleteTitle')}
                    >
                      {deletingDraftId === draft.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                    {draft.status === 'done' && (
                      <ChevronRight size={18} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-start min-w-0">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1 || loading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
              {t('jobDescription.draftsModal.pagination.previous')}
            </button>
            <div className="text-sm font-medium text-gray-600 min-w-[92px] text-center">
              {t('jobDescription.draftsModal.pagination.pageInfo', { page, totalPages })}
            </div>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages || loading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('jobDescription.draftsModal.pagination.next')}
              <ChevronRight size={16} />
            </button>
          </div>

          <button
            onClick={onClose}
            className="w-full sm:w-auto sm:min-w-[120px] self-stretch sm:self-auto shrink-0 px-5 py-2.5 font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            {t('jobDescription.audioModal.buttons.cancel')}
          </button>
        </div>
      </div>

      <ConfirmationDialog
        open={Boolean(draftPendingDelete)}
        onClose={closeDeleteConfirmation}
        onConfirm={handleDeleteDraft}
        title={t('jobDescription.draftsModal.actions.confirmDeleteTitle')}
        message={t('jobDescription.draftsModal.actions.confirmDeleteMessage', { name: getDraftDisplayName(draftPendingDelete, t) })}
      />
    </div>
  );
}
